"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { apiJson } from "@/lib/api-client";
import {
  isAbortError,
  LOGIN_FETCH_TIMEOUT_MS,
  loginLandingPath,
  readStoredLoginRedirect,
} from "@/lib/login-redirect";
import { clearSavedLogin, saveLogin } from "@/lib/saved-login";

interface AuthUser {
  id: number;
  email: string;
  name?: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  forcePasswordReset: boolean;
  phone?: string | null;
  approved?: boolean;
  typeId?: number;
  classificationId?: number | null;
  companyId?: number;
  lastLoginAt?: string | null;
  loginCount?: number;
  isActive?: boolean;
}

interface MeResponse {
  user: AuthUser;
  groups: { id: number; groupName: string; category: string; isAdminGroup: boolean; isGroupAdmin: boolean }[];
  security: {
    isAdmin: boolean;
    groupIds: number[];
    privilegeCount: number;
    domainAdminMenuKeys?: string[];
  } | null;
  preferences?: {
    alertBellSoundEnabled: boolean;
    alertBellFieldDefId: number | null;
  };
  /**
   * Menu keys the current user is DENIED (blacklist model).
   * Empty array = no restrictions configured = user sees everything.
   * canSee(key) = !menuDenied.includes(key)
   */
  menuDenied: string[];
}

export function useCurrentUser(opts?: { enabled?: boolean }) {
  return useQuery<MeResponse>({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (!res.ok) throw new Error("Not authenticated");
      return res.json();
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
    enabled: opts?.enabled !== false,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: async ({
      email,
      password,
      rememberMe,
    }: {
      email: string;
      password: string;
      rememberMe?: boolean;
    }) => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), LOGIN_FETCH_TIMEOUT_MS);
      try {
        const staySignedIn = rememberMe === true;
        const data = await apiJson<{ user: AuthUser }>("/api/auth/login", {
          method: "POST",
          credentials: "include",
          body: { email, password, rememberMe: staySignedIn },
          signal: controller.signal,
        });
        if (staySignedIn) saveLogin(email, password);
        else clearSavedLogin();
        return data.user;
      } catch (err) {
        if (isAbortError(err)) {
          throw new Error("Sign-in timed out. Check your connection and try again.");
        }
        throw err;
      } finally {
        window.clearTimeout(timer);
      }
    },
    onSuccess: (user) => {
      // Full load so the session cookie is stored before the next page — iOS
      // Safari often drops a client router.push that races Set-Cookie.
      window.location.assign(
        loginLandingPath(user.forcePasswordReset, window.location.search, readStoredLoginRedirect()),
      );
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: async () => {
      await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    },
    onSuccess: () => {
      qc.clear();
      router.push("/login");
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
      return apiJson("/api/auth/change-password", {
        method: "POST",
        credentials: "include",
        body: { currentPassword, newPassword },
      });
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      return apiJson("/api/auth/forgot-password", {
        method: "POST",
        body: { email },
      });
    },
  });
}

export function useResetPassword() {
  const router = useRouter();

  return useMutation({
    mutationFn: async ({ token, newPassword }: { token: string; newPassword: string }) => {
      return apiJson("/api/auth/reset-password", {
        method: "POST",
        body: { token, newPassword },
      });
    },
    onSuccess: () => {
      router.push("/login");
    },
  });
}
